// Workflow Presets — per-user library of saved node subgraphs.
//
// A preset is a full snapshot of a chunk of the canvas: per-node AI tool
// (the existing node.workflowId field), per-node personality binding,
// inspector inputs, exact spatial layout (relative to a designated root
// node), and all internal wires. Presets are captured ("Save selection as
// workflow", Phase 2) and dispatched ("spawn from a node", Phase 3) via
// VybyWorkflowsTrigger in the node-canvas inspector.
//
// Naming note: the field on each saved-graph node that points to the AI
// tool is intentionally still called `workflowId` to match the live
// node.workflowId field elsewhere in the codebase. The new concept's id
// is `workflowPresetId` (or just `id` on the preset row itself); the two
// never collide.

// Categories use the same set as personalities so the chip dropdown UX
// stays consistent (intentional decision — see plan).
export const WORKFLOW_PRESET_CATEGORIES = [
  'Designing',
  'Cloning',
  'Clothing Haul',
  'Other',
]
export const DEFAULT_WORKFLOW_PRESET_CATEGORY = 'Other'

// Soft cap on saved-graph size. Mostly a sanity guard against accidentally
// "save selection" with a huge marquee. UI surfaces this as a warning;
// the normalizer doesn't truncate. Tunable.
export const MAX_PRESET_NODES = 64

export function createWorkflowPresetId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `preset-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

export function normalizeWorkflowPresetCategory(value) {
  const raw = `${value || ''}`.trim()
  if (!raw) return DEFAULT_WORKFLOW_PRESET_CATEGORY
  const match = WORKFLOW_PRESET_CATEGORIES.find(
    (c) => c.toLowerCase() === raw.toLowerCase(),
  )
  return match || DEFAULT_WORKFLOW_PRESET_CATEGORY
}

function nowIso() {
  try {
    return new Date().toISOString()
  } catch (e) {
    return ''
  }
}

// ---------------------------------------------------------------------------
// Preset shape
// ---------------------------------------------------------------------------
//
// {
//   id: string,                       // crypto.randomUUID()
//   name: string,
//   description: string,
//   category: string,                 // one of WORKFLOW_PRESET_CATEGORIES
//   createdAt: string,                // ISO
//   updatedAt: string,                // ISO
//   graph: {
//     rootNodeKey: string,            // which saved-node is the spawn anchor
//     nodes: [{
//       key: string,                  // local-only id, used by edges
//       relativeX: number,            // world-space, relative to root's X
//       relativeY: number,            // world-space, relative to root's Y
//       workflowId: string|null,      // the AI tool (existing node field)
//       groupId: string|null,
//       personalityId: string|null,   // binding only — not deep-copied
//       personalityOverride: boolean,
//       moodId: string|null,          // binding only — not deep-copied
//       inputs: object,               // verbatim node.inputs snapshot
//       settings: object,             // verbatim node.settings snapshot
//     }],
//     edges: [{
//       fromKey: string,              // saved-node key, not a live node id
//       fromOutputIdx: number,
//       toKey: string,
//       mapping: object,              // verbatim edge.mapping snapshot
//     }],
//   },
// }
//
// Phase 1 only stores/normalizes this shape; capture (Phase 2) and dispatch
// (Phase 3) consume it. The normalizer is forgiving — partial graphs round-
// trip safely so dev seeding and future schema additions don't break older
// rows.

function normalizeGraphNode(entry, index) {
  if (!entry || typeof entry !== 'object') return null
  const key = `${entry.key || ''}`.trim() || `n${index + 1}`
  const relativeX = Number.isFinite(Number(entry.relativeX)) ? Number(entry.relativeX) : 0
  const relativeY = Number.isFinite(Number(entry.relativeY)) ? Number(entry.relativeY) : 0
  return {
    key,
    relativeX,
    relativeY,
    workflowId: entry.workflowId ? `${entry.workflowId}`.trim() : null,
    groupId: entry.groupId ? `${entry.groupId}`.trim() : null,
    personalityId: entry.personalityId ? `${entry.personalityId}`.trim() : null,
    personalityOverride: !!entry.personalityOverride,
    moodId: entry.moodId ? `${entry.moodId}`.trim() : null,
    inputs:
      entry.inputs && typeof entry.inputs === 'object' ? { ...entry.inputs } : {},
    settings:
      entry.settings && typeof entry.settings === 'object'
        ? { ...entry.settings }
        : {},
  }
}

function normalizeGraphEdge(entry) {
  if (!entry || typeof entry !== 'object') return null
  const fromKey = `${entry.fromKey || ''}`.trim()
  const toKey = `${entry.toKey || ''}`.trim()
  if (!fromKey || !toKey) return null
  const fromOutputIdx = Number.isFinite(Number(entry.fromOutputIdx))
    ? Math.max(0, Math.floor(Number(entry.fromOutputIdx)))
    : 0
  return {
    fromKey,
    fromOutputIdx,
    toKey,
    mapping:
      entry.mapping && typeof entry.mapping === 'object'
        ? { ...entry.mapping }
        : {},
  }
}

function normalizeGraph(raw) {
  const obj = raw && typeof raw === 'object' ? raw : {}
  const nodes = Array.isArray(obj.nodes) ? obj.nodes : []
  const edges = Array.isArray(obj.edges) ? obj.edges : []
  const normNodes = nodes
    .map((n, i) => normalizeGraphNode(n, i))
    .filter((n) => !!n)
  const validKeys = new Set(normNodes.map((n) => n.key))
  const normEdges = edges
    .map(normalizeGraphEdge)
    .filter((e) => e && validKeys.has(e.fromKey) && validKeys.has(e.toKey))
  // rootNodeKey defaults to the first node's key when missing or stale.
  const rootRaw = `${obj.rootNodeKey || ''}`.trim()
  const rootNodeKey =
    rootRaw && validKeys.has(rootRaw)
      ? rootRaw
      : normNodes[0]
        ? normNodes[0].key
        : ''
  return {
    rootNodeKey,
    nodes: normNodes,
    edges: normEdges,
  }
}

export function normalizeWorkflowPreset(item, index = 0) {
  if (!item || typeof item !== 'object') return null
  const idRaw = `${item.id || ''}`.trim()
  const nameRaw = `${item.name || ''}`.trim()
  const descRaw = typeof item.description === 'string' ? item.description : ''
  const category = normalizeWorkflowPresetCategory(item.category)
  const createdAt = `${item.createdAt || ''}`.trim() || nowIso()
  const updatedAt = `${item.updatedAt || ''}`.trim() || createdAt
  const graph = normalizeGraph(item.graph)
  return {
    id: idRaw || `preset-${index + 1}`,
    name: nameRaw || `Preset ${index + 1}`,
    description: descRaw,
    category,
    createdAt,
    updatedAt,
    graph,
  }
}

export function normalizeAiWorkflowPresets(list) {
  if (!Array.isArray(list)) return []
  const seen = new Set()
  return list.reduce((acc, item, index) => {
    const next = normalizeWorkflowPreset(item, index)
    if (!next || seen.has(next.id)) return acc
    seen.add(next.id)
    acc.push(next)
    return acc
  }, [])
}

// Group presets by their category. Used by the standalone Workflows chip's
// 2-column dropdown (right column = presets in active category).
export function groupPresetsByCategory(list) {
  const out = WORKFLOW_PRESET_CATEGORIES.reduce((acc, c) => {
    acc[c] = []
    return acc
  }, {})
  if (!Array.isArray(list)) return out
  list.forEach((p) => {
    if (!p) return
    const c = normalizeWorkflowPresetCategory(p.category)
    if (out[c]) out[c].push(p)
  })
  return out
}

// Sorts presets newest-first by updatedAt (then createdAt). Useful for the
// dropdown's recency-first ordering.
export function sortPresetsRecent(list) {
  if (!Array.isArray(list)) return []
  return [...list].sort((a, b) => {
    const au = `${a?.updatedAt || a?.createdAt || ''}`
    const bu = `${b?.updatedAt || b?.createdAt || ''}`
    if (au === bu) return 0
    return au < bu ? 1 : -1
  })
}
